export type AppRole = "SystemAdmin" | "ContentCreator" | "TeamLeader" | "Student";

export const APP_ROLES: AppRole[] = ["SystemAdmin", "ContentCreator", "TeamLeader", "Student"];

export type PermissionName =
  | "view:analytics"
  | "view:roster"
  | "grade:submissions"
  | "manage:courses"
  | "issue:certificates"
  | "view:certificates"
  | "view:crm"
  | "manage:users"
  | "manage:teams"
  // Scoped group management for a TeamLeader: manage ONLY the groups they lead (membership,
  // training assignment, reset, scoped certificate issuance, group messaging). Distinct from
  // manage:teams (SystemAdmin-only: create/delete groups + leader assignment across the org).
  | "manage:my-groups"
  | "manage:roles"
  // Display-only: never granted through ROLE_PERMISSIONS/hasPermission. The real gate for the
  // monitoring pages is the isSuperAdmin boolean (see hasSuperAdminRole) — this entry exists only
  // so the Roles & Permissions matrix on /admin/roles can show what Super Administrator adds on
  // top of System Administrator.
  | "monitor:staff"
  // ── Recycle Bin ───────────────────────────────────────────────────────────
  // Mirrors the backend's AppPermissions/RolePermissions, which authorizes these endpoints by
  // capability rather than by role. Named identically on both sides on purpose: a permission that
  // reads the same in a C# attribute, an HTTP response and this catalog is one nobody has to
  // translate. Viewing, restoring and permanently destroying are three different levels of trust,
  // which a single "manage:users" could not express.
  | "trash.view"
  | "trash.restore"
  | "trash.hard-delete"
  | "trash.bulk-restore"
  | "trash.bulk-delete"
  // Display-only here, like monitor:staff: writing to the Users tab of the Recycle Bin is
  // SuperAdmin-only on the server, enforced by the entity's own descriptor. Listed so the roles
  // matrix can show it; never granted through ROLE_PERMISSIONS.
  | "trash.users.write";

export const ROLE_PERMISSIONS: Record<AppRole, PermissionName[]> = {
  SystemAdmin: [
    "view:analytics",
    "manage:users",
    "manage:courses",
    "view:roster",
    "issue:certificates",
    "view:certificates",
    "view:crm",
    "manage:teams",
    "manage:my-groups",
    "manage:roles",
    "grade:submissions",
    "trash.view",
    "trash.restore",
    "trash.hard-delete",
    "trash.bulk-restore",
    "trash.bulk-delete",
  ],
  // ContentCreator builds courses and exams, and sees analytics ONLY for the tracks/courses/
  // modules/exams they authored — the backend (AnalyticsViewers + AccessScopeService) scopes every
  // analytics endpoint to CreatedByAdminId == caller, so there is no organization-wide view. They do
  // NOT get view:roster, view:certificates, view:crm, manage:users, manage:teams or manage:roles —
  // those administrative/reporting surfaces stay hidden.
  ContentCreator: ["manage:courses"],
  // view:crm mirrors the backend /leads authz (SystemAdmin + TeamLeader only) so the Leads Funnel
  // card isn't shown to — and 403'd for — ContentCreators who also hold view:analytics.
  // manage:my-groups unlocks the scoped group-management surface on the "My Team" area (backed by
  // api/teamleader/*), which the backend gates per-group via AccessScopeService.CanManageGroupAsync.
  // grade:submissions/view:analytics/view:roster: TeamLeader absorbed the former Instructor role —
  // academic/course supervision (grading, per-course analytics) is now part of TeamLeader alongside
  // group management.
  TeamLeader: [
    "view:analytics",
    "view:roster",
    "grade:submissions",
    "view:certificates",
    "view:crm",
    "manage:my-groups",
  ],
  Student: [],
};

export const PERMISSION_CATALOG: { name: PermissionName; label: string; description: string }[] = [
  {
    name: "view:analytics",
    label: "View Analytics",
    description: "See dashboards, rosters and live monitoring (scoped to assigned courses/groups).",
  },
  {
    name: "view:roster",
    label: "View Roster",
    description: "Browse the student list for their assigned courses/groups.",
  },
  {
    name: "grade:submissions",
    label: "Grade Submissions",
    description: "Grade essay responses and access the grading queue.",
  },
  {
    name: "manage:courses",
    label: "Manage Courses & Tracks",
    description: "Author courses, tracks, modules and exam banks.",
  },
  {
    name: "issue:certificates",
    label: "Issue Certificates",
    description: "Generate, revoke and manage certificates.",
  },
  {
    name: "view:certificates",
    label: "View Certificates",
    description: "Read-only view of certificates list and events.",
  },
  {
    name: "view:crm",
    label: "View CRM / Leads",
    description: "See the leads funnel and CRM analytics (scoped to owned leads).",
  },
  {
    name: "manage:users",
    label: "Manage Users",
    description: "Create/edit/delete users and reset progress.",
  },
  {
    name: "manage:teams",
    label: "Manage Teams",
    description: "Manage groups, batches and team-leader assignments.",
  },
  {
    name: "manage:my-groups",
    label: "Manage My Groups",
    description: "Manage membership, training and certificates for the groups you lead.",
  },
  {
    name: "manage:roles",
    label: "Manage Roles",
    description: "View and administer role assignments.",
  },
  {
    name: "monitor:staff",
    label: "Monitor Staff Activity",
    description:
      "Real-time employee activity, session history and the Team Leader leaderboard. Super Administrator only.",
  },
  {
    name: "trash.view",
    label: "View Recycle Bin",
    description: "Browse deleted records and preview what restoring or deleting one would affect.",
  },
  {
    name: "trash.restore",
    label: "Restore Deleted Records",
    description: "Bring a deleted record back, along with anything deleted with it.",
  },
  {
    name: "trash.hard-delete",
    label: "Permanently Delete Records",
    description: "Destroy a deleted record for good. This cannot be undone.",
  },
  {
    name: "trash.bulk-restore",
    label: "Bulk Restore",
    description: "Restore many records in one action.",
  },
  {
    name: "trash.bulk-delete",
    label: "Bulk Permanent Delete",
    description:
      "Permanently destroy many records in one action. Separate from single deletion because the blast radius of one mistaken click is far larger.",
  },
  {
    name: "trash.users.write",
    label: "Restore User Accounts",
    description:
      "Restore deleted accounts. Super Administrator only: course, group and enrolment assignments survive a deletion, so a restored account regains the access it had.",
  },
];

/** Permissions Super Administrator adds on top of every System Administrator permission. */
export const SUPER_ADMIN_EXTRA_PERMISSIONS: PermissionName[] = [
  "monitor:staff",
  "trash.users.write",
];

export function normalizeRole(role: string | undefined | null): AppRole | null {
  if (!role) return null;
  const r = role
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "");
  if (r === "systemadmin" || r === "admin" || r === "superadmin" || r === "super_admin") {
    return "SystemAdmin";
  }
  if (r === "contentcreator") {
    return "ContentCreator";
  }
  // Instructor was merged into TeamLeader — any stale/legacy backend claim still using this
  // string resolves to TeamLeader rather than being dropped.
  if (r === "instructor" || r === "ta") {
    return "TeamLeader";
  }
  if (r === "teamleader" || r === "teamlead" || r === "team_leader" || r === "team_lead") {
    return "TeamLeader";
  }
  if (r === "student") {
    return "Student";
  }
  return null;
}

/**
 * True when the raw role claims include the Principal Supervisor role.
 *
 * Checked against the RAW claim on purpose: `normalizeRole` deliberately folds "superadmin" into
 * "SystemAdmin" so a SuperAdmin inherits every SystemAdmin permission (matching the backend, which
 * grants both roles). That fold makes the two indistinguishable, so the monitoring pages — the one
 * place where the distinction matters — need this instead.
 *
 * This is a UI affordance only. The real boundary is the server's
 * [Authorize(Roles = SuperAdmin)] on /api/staff-monitoring/*; hiding a link never protects data.
 */
export function hasSuperAdminRole(userRoles: string[]): boolean {
  return userRoles.some((role) => {
    const r = (role ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    return r === "superadmin";
  });
}

/**
 * The ownership fields any authored row carries (exam, course, track, question bank), under either
 * casing the API/mapper may produce.
 */
export interface OwnedContent {
  createdByAdminId?: string | null;
  created_by?: string | null;
}

/** Back-compat alias — exams were the first surface to gate on ownership. */
export type ExamOwnership = OwnedContent;

/** The row's author id, or null when none is recorded. */
export function contentOwnerId(content: OwnedContent | null | undefined): string | null {
  if (!content) return null;
  return content.createdByAdminId ?? content.created_by ?? null;
}

/** @deprecated Use {@link contentOwnerId} — kept so existing exam call sites keep compiling. */
export const examOwnerId = contentOwnerId;

/**
 * True when a row records no author.
 *
 * This no longer means "claimable". Ownerless content used to be editable by any authoring role,
 * with the first writer claiming it; that hatch is gone (see {@link canManageContent}), and the
 * ownerless exams it existed for are being backfilled from the audit log. Only an admin can still
 * write one, which is what claims it.
 *
 * Retained as a diagnostic — "this row predates authorship tracking" — not as a permission signal.
 * Do not gate an Edit button on it.
 */
export function isUnclaimedExam(content: OwnedContent | null | undefined): boolean {
  return contentOwnerId(content) === null;
}

/**
 * May this caller modify this content (edit / delete / assign)?
 *
 * Mirrors the server's IContentOwnershipService after the strict-own-edit change, so the UI offers
 * exactly what the API would accept:
 *   • SystemAdmin / SuperAdmin — always;
 *   • otherwise ONLY the author (`CreatedByAdminId == caller`).
 *
 * Note what is deliberately NOT here: reach. Any ContentCreator may READ every course, track,
 * module, exam and bank on the platform — that is why this helper exists at all. Seeing something is
 * no longer a hint that you may change it, so every authoring surface has to ask this per row.
 *
 * An ownerless row (`owner === null`) is NOT editable by a non-admin. It used to be — "legacy rows
 * are editable by anyone, first writer claims them" — but 59% of live exams were ownerless, which in
 * practice meant every staff member could edit and delete hundreds of exams, including live course
 * gates with student attempts behind them. Ownership is recovered by backfill instead.
 *
 * An affordance, NOT a security boundary: the 403 on the write endpoint is the real gate, and hiding
 * a button never protects data.
 *
 * A null/undefined row means "creating a new one", which is always allowed.
 */
export function canManageContent(
  caller: { isAdmin: boolean; userId?: string | null },
  content: OwnedContent | null | undefined,
): boolean {
  if (!content) return true; // creating new
  if (caller.isAdmin) return true;
  const owner = contentOwnerId(content);
  if (owner === null) return false;
  // GUIDs arrive from a JWT claim on one side and JSON on the other; casing is not guaranteed.
  return !!caller.userId && owner.toLowerCase() === caller.userId.toLowerCase();
}

/** May this caller edit/delete this exam? See {@link canManageContent}. */
export const canEditExam = canManageContent;

/** May this caller edit/delete this course? See {@link canManageContent}. */
export const canManageCourse = canManageContent;

/** May this caller edit/delete this track? See {@link canManageContent}. */
export const canManageTrack = canManageContent;

/** May this caller edit/delete this question bank? See {@link canManageContent}. */
export const canManageBank = canManageContent;

/**
 * May this caller edit/delete this module? Modules carry no author of their own — authorship is
 * inherited from the parent course (mirroring ModuleService.EnsureCanManageCourseAsync), so pass the
 * course the module belongs to.
 */
export function canManageModule(
  caller: { isAdmin: boolean; userId?: string | null },
  parentCourse: OwnedContent | null | undefined,
): boolean {
  return canManageContent(caller, parentCourse);
}

export function hasAnyRole(userRoles: string[], ...allowedRoles: AppRole[]): boolean {
  const normalizedUserRoles = userRoles.map(normalizeRole).filter((r): r is AppRole => r !== null);
  return allowedRoles.some((allowed) => normalizedUserRoles.includes(allowed));
}

/** The fields of a chat channel that decide who may post in it. */
export interface ChatChannel {
  /** "team" (a group discussion), "announcement", "course" or "dm". */
  type: string;
  is_read_only?: boolean;
  /** Set on a project-scoped announcement channel. */
  project?: string | null;
  /** Set on a batch-scoped announcement channel. */
  batch?: string | null;
}

/**
 * May this caller write in this chat channel?
 *
 * Mirrors what the server authorizes in MessageService.CreateMessageAsync so the composer is shown
 * exactly where a POST /api/messages would succeed:
 *
 *   • the two platform-wide channels (general-discussion, announcements) are read-open to the
 *     whole platform and writable by a SystemAdmin alone — not by team leaders — because a post
 *     there reaches every batch at once; students take part in their own batch channels instead;
 *   • announcement / course channels are one-way — GroupAnnouncement is admin-only (batch-scoped
 *     also admits team leaders) and CourseAnnouncement admits only the course's team leader, which
 *     the server expresses to us as `is_read_only` on the reader's copy of the channel;
 *   • a "team" channel is a GroupDiscussion channel, and CanAccessChannelAsync admits any leader
 *     OR enrolled member of the group — so it is a two-way conversation for STUDENTS TOO, and this
 *     must not be narrowed to the group's leader;
 *   • DMs are writable by both participants (the server only ever lists a DM to its two members).
 *
 * An affordance, not a security boundary: the 403/400 from the send endpoint is the real gate.
 */
export function canPostInChannel(
  caller: { isAdmin: boolean; isTeamLeader: boolean },
  channel: ChatChannel | null | undefined,
): boolean {
  if (!channel) return false;
  if (caller.isAdmin) return true;

  // Project announcement: admins only.
  if (channel.type === "announcement" && channel.project && !channel.batch) return false;

  // Batch announcement: admins + team leaders.
  if (channel.type === "announcement" && channel.batch) return caller.isTeamLeader;

  // Group discussion: every member of the group, students included. Honors is_read_only so a
  // server-side lock still takes effect.
  if (channel.type === "team") return !channel.is_read_only;

  // Everything else — the platform-wide pair, group and course announcements, DMs. The server
  // already resolves each caller's own posting right into is_read_only on their copy of the
  // channel, so deferring to it is what keeps this affordance from drifting off the send gate.
  return !channel.is_read_only;
}
